import { motion } from 'framer-motion';
import { CountUp } from '@/components/roi/CountUp';
import { MarketSize } from '@/components/roi/MarketSize';
import { International } from '@/components/roi/International';
import {
  UK_SAM_ARR,
  UK_SOM_ARR,
  UK_TAM_HEADLINE_ARR,
  formatARR,
} from '@/lib/roi/market-data';
import { Disclosure } from './Disclosure';
import { ScaleBar } from './ScaleBar';
import { SectionShell } from './SectionShell';

/**
 * Compressed TAM / SAM / SOM. Three big numbers, three deep-link CTAs
 * into /roi#market and /roi#international. Doesn't repeat the segment
 * tables — that's what /roi is for.
 */

const STAGES: {
  id: string;
  acronym: string;
  title: string;
  value: number;
  highValue?: number;
  caption: string;
  href: string;
}[] = [
  {
    id: 'tam',
    acronym: 'TAM',
    title: 'UK I&R Market',
    value: UK_TAM_HEADLINE_ARR,
    caption: 'UK insolvency and restructuring software opportunity',
    href: '/roi#market',
  },
  {
    id: 'sam',
    acronym: 'SAM',
    title: 'Serviceable addressable',
    value: UK_SAM_ARR,
    caption: 'UK firms realistically targetable in the first 5 to 7 years',
    href: '/roi#market',
  },
  {
    id: 'som',
    acronym: 'SOM',
    title: 'Serviceable obtainable',
    value: UK_SOM_ARR,
    caption: 'Realistic capture within first 3 years',
    href: '/roi#market',
  },
];

export function MarketSummary() {
  return (
    <SectionShell
      id="market"
      index={5}
      eyebrow="Market opportunity"
      title={
        <>
          A{' '}
          <span className="text-brand-bright">
            {formatARR(UK_TAM_HEADLINE_ARR)}
          </span>{' '}
          UK market with <br className="hidden sm:block" />
          international expansion behind it
        </>
      }
      intro="UK insolvency and restructuring is a contained, high margin software market with a well defined international expansion path"
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {STAGES.map((s, i) => (
          <StageCard key={s.id} stage={s} index={i} />
        ))}
      </div>

      <Disclosure
        label="See the UK market breakdown"
        expandedLabel="Hide the UK breakdown"
      >
        <MarketSize embedded />
      </Disclosure>

      <Disclosure
        label="See international expansion markets"
        expandedLabel="Hide international markets"
      >
        <International embedded />
      </Disclosure>
    </SectionShell>
  );
}

function StageCard({
  stage,
  index,
}: {
  stage: (typeof STAGES)[number];
  index: number;
}) {
  return (
    <motion.a
      href={stage.href}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -3 }}
      transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.08,
      }}
      className="pitch-card pitch-card-interactive relative isolate flex flex-col gap-4 overflow-hidden rounded-2xl p-7 sm:p-8"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-24 -z-10 h-48 w-48 rounded-full opacity-[0.10] blur-2xl"
        style={{ background: '#3B82F6' }}
      />
      <span className="inline-flex w-fit items-center rounded-md border border-accent-blue-border bg-accent-blue-bg px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-brand-bright">
        {stage.acronym}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className={`whitespace-nowrap font-semibold tabular-nums text-brand-bright [line-height:0.95] ${
            stage.highValue !== undefined
              ? '[font-size:32px] sm:[font-size:40px] lg:[font-size:30px] xl:[font-size:34px]'
              : '[font-size:52px] sm:[font-size:64px]'
          }`}
        >
          {stage.highValue !== undefined ? (
            <>
              £
              <CountUp
                target={stage.value}
                duration={1.4}
                delay={0.3}
                formatter={formatHeadlineValue}
              />
              {' to '}
              <CountUp
                target={stage.highValue}
                duration={1.4}
                delay={0.45}
                formatter={formatHeadlineValue}
              />
              m
            </>
          ) : (
            <>
              £
              <CountUp
                target={stage.value}
                duration={1.6}
                delay={0.3}
                formatter={formatHeadlineValue}
              />
              m
            </>
          )}
        </span>
        <span className="text-[12px] text-ink-dim">ARR</span>
      </div>
      {/* These are equal visual rules for the three summary cards, not
          proportional data bars. The figures above retain the TAM / SAM /
          SOM relationship; a shared full-width rule keeps the card row
          visually consistent and avoids reading as a loading indicator. */}
      <ScaleBar to={1} delay={0.35 + index * 0.08} />

      <span className="text-[14px] font-medium text-ink-strong">
        {stage.title}
      </span>
      <p className="text-[13px] leading-relaxed text-ink-muted">
        {stage.caption}
      </p>
    </motion.a>
  );
}

function formatHeadlineValue(value: number): string {
  const rounded = Math.round((value + Number.EPSILON) * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
}
